import { useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ConnectionState } from 'livekit-client'
import { usePolling } from '../hooks/usePolling'
import { useLiveKit } from '../hooks/useLiveKit'
import { partyApi, PartyDetail, PartyStatus } from '../lib/api'
import QRCodeDisplay from '../components/QRCodeDisplay'
import CommandBar from '../components/CommandBar'
import VideoGrid from '../components/VideoGrid'
import ParticipantList from '../components/ParticipantList'

const statusColors: Record<PartyStatus, string> = {
  waiting: 'bg-yellow-100 text-yellow-800',
  active: 'bg-green-100 text-green-800',
  paused: 'bg-orange-100 text-orange-800',
  stopped: 'bg-red-100 text-red-800',
  ended: 'bg-gray-100 text-gray-600',
}

const connectionLabel: Record<string, { text: string; color: string }> = {
  [ConnectionState.Connected]: { text: 'Connected', color: 'text-green-600' },
  [ConnectionState.Connecting]: { text: 'Connecting…', color: 'text-yellow-600' },
  [ConnectionState.Reconnecting]: { text: 'Reconnecting…', color: 'text-yellow-600' },
  [ConnectionState.Disconnected]: { text: 'Disconnected', color: 'text-gray-400' },
}

export default function PartyDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const fetcher = useCallback(() => partyApi.get(id!), [id])
  const { data: party, loading, error, refetch } = usePolling<PartyDetail>({ fetcher, interval: 5000, enabled: !!id })

  const liveKitEnabled = !!party && party.status !== 'ended'
  const { room, connectionState, error: lkError, reconnect } = useLiveKit({
    partyCode: party?.code ?? '',
    enabled: liveKitEnabled,
  })

  const handleEnd = async () => {
    if (!id || !confirm(`End party "${party?.name}"?`)) return
    try {
      await partyApi.end(id)
      refetch()
    } catch {
      // will refresh via polling
    }
  }

  if (loading && !party) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/3 animate-pulse"></div>
        <div className="bg-white shadow rounded-lg p-6 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
        </div>
      </div>
    )
  }

  if (error || !party) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">Failed to load party</p>
        <button onClick={() => navigate('/')} className="text-brand-600 hover:text-brand-700 font-medium">
          Back to Dashboard
        </button>
      </div>
    )
  }

  const connInfo = connectionLabel[connectionState] ?? connectionLabel[ConnectionState.Disconnected]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{party.name}</h1>
          <div className="flex items-center gap-3 mt-1">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[party.status]}`}>
              {party.status}
            </span>
            {liveKitEnabled && (
              <>
                <span className={`text-xs ${connInfo.color}`}>
                  {connInfo.text}
                </span>
                {connectionState === ConnectionState.Disconnected && (
                  <button
                    type="button"
                    onClick={reconnect}
                    className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                  >
                    Reconnect
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        {party.status !== 'ended' && (
          <button
            onClick={handleEnd}
            className="inline-flex items-center px-4 py-2 border border-red-300 text-red-700 text-sm font-medium rounded-md hover:bg-red-50"
          >
            End Party
          </button>
        )}
      </div>

      {/* Command Bar */}
      {party.status !== 'ended' && (
        <CommandBar partyId={party.id} status={party.status} onCommandSent={refetch} />
      )}

      {/* LiveKit Error */}
      {lkError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center justify-between">
          <span>LiveKit: {lkError}</span>
          <button
            type="button"
            onClick={reconnect}
            className="ml-3 text-red-700 font-medium hover:text-red-900 underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Video Grid */}
      {room && <VideoGrid room={room} />}

      {/* Info Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Details */}
        <div className="bg-white shadow rounded-lg p-5 space-y-3">
          <h2 className="text-lg font-semibold text-gray-900">Details</h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Join Code</dt>
              <dd className="font-mono font-medium text-gray-900">{party.code}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Max Participants</dt>
              <dd>{party.max_participants}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Created</dt>
              <dd>{new Date(party.created_at).toLocaleString()}</dd>
            </div>
            {party.ended_at && (
              <div className="flex justify-between">
                <dt className="text-gray-500">Ended</dt>
                <dd>{new Date(party.ended_at).toLocaleString()}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* QR Code */}
        {party.status !== 'ended' && (
          <div className="bg-white shadow rounded-lg p-5 flex flex-col items-center">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">QR Code</h2>
            <QRCodeDisplay partyId={party.id} size={200} />
            <p className="mt-2 text-xs text-gray-500">Scan to join the party</p>
          </div>
        )}
      </div>

      {/* Participants */}
      <ParticipantList
        participants={party.participants}
        partyId={party.id}
        partyStatus={party.status}
        onAction={refetch}
      />
    </div>
  )
}
